import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Alert } from "react-bootstrap";
import {
  getPausedServices,
  createPause,
  resumeService,
  cancelScheduledPause,
  clearError,
  removePausedServiceOptimistic,
  addPausedServiceOptimistic,
  removeScheduledPauseOptimistic,
  addScheduledPauseOptimistic
} from "../features/availability/availabilitySlice";
import addressIcon from "../assets/images/Formicons/address_icon.svg";
import scheduleIcon from "../assets/images/Formicons/shedule.svg";
import timeIcon from "../assets/images/Formicons/time_duration.svg";
import CustomAlert from "../components/CustomAlert";
import "../css/Availability.css";
import { ShimmerBox } from "../components/Shimmer";
import servicesEmptyIcon from '../assets/images/EmptyIcons/Services-Empty.svg';
import "../css/Shimmer.css";
import checkedCheckboxIcon from '../assets/images/checked-checkbox-.svg';
import uncheckedCheckboxIcon from '../assets/images/uncheck-checkbox-outline.svg';

const Availability = () => {
  const dispatch = useDispatch();
  const { pausedServices, scheduledPauses, availableServices, isLoading, error } = useSelector(
    (state) => state.availability
  );

  // State for custom alerts
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ type: 'success', title: '', message: '' });

  // State for pause type
  const [pauseType, setPauseType] = useState("immediate");

  // State for immediate pause
  const [selectedDuration, setSelectedDuration] = useState("");
  const [showDurationDropdown, setShowDurationDropdown] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);

  // State for future pause
  const [startDate, setStartDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState("");
  const [endTime, setEndTime] = useState("");
  const [futureServices, setFutureServices] = useState([]);

  // Inline pickers state
  const [showStartDatePicker, setShowStartDatePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndDatePicker, setShowEndDatePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  // Date picker states
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);

  // Time picker states
  const [startHour, setStartHour] = useState("07");
  const [startMinute, setStartMinute] = useState("15");
  const [startPeriod, setStartPeriod] = useState("AM");
  const [endHour, setEndHour] = useState("07");
  const [endMinute, setEndMinute] = useState("15");
  const [endPeriod, setEndPeriod] = useState("AM");

  // Refs for click outside detection
  const durationRef = useRef(null);
  const serviceRef = useRef(null);
  const startDateRef = useRef(null);
  const startTimeRef = useRef(null);
  const endDateRef = useRef(null);
  const endTimeRef = useRef(null);

  // Get available services from API
  const services = availableServices.map(service => ({
    id: service.ServiceId,
    name: service.ServiceName
  }));

  // Duration options
  const durations = [
    { id: 1, label: "Next 30 Minutes", value: "30min" },
    { id: 2, label: "Next 60 Minutes", value: "60min" },
    { id: 3, label: "Next 2 Hours", value: "2hr" },
    { id: 4, label: "Next 4 Hours", value: "4hr" },
    { id: 5, label: "Next 6 Hours", value: "6hr" },
  ];

  // Fetch paused services on component mount
  useEffect(() => {
    dispatch(getPausedServices());
  }, [dispatch]);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  // Click outside to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (durationRef.current && !durationRef.current.contains(event.target)) {
        setShowDurationDropdown(false);
      }
      if (serviceRef.current && !serviceRef.current.contains(event.target)) {
        setShowServiceDropdown(false);
      }
      if (
        startDateRef.current &&
        !startDateRef.current.contains(event.target)
      ) {
        setShowStartDatePicker(false);
      }
      if (
        startTimeRef.current &&
        !startTimeRef.current.contains(event.target)
      ) {
        setShowStartTimePicker(false);
      }
      if (endDateRef.current && !endDateRef.current.contains(event.target)) {
        setShowEndDatePicker(false);
      }
      if (endTimeRef.current && !endTimeRef.current.contains(event.target)) {
        setShowEndTimePicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Toggle service selection
  const toggleServiceSelection = (serviceId) => {
    if (pauseType === "immediate") {
      if (selectedServices.includes(serviceId)) {
        setSelectedServices(selectedServices.filter((id) => id !== serviceId));
      } else {
        setSelectedServices([...selectedServices, serviceId]);
      }
    } else {
      if (futureServices.includes(serviceId)) {
        setFutureServices(futureServices.filter((id) => id !== serviceId));
      } else {
        setFutureServices([...futureServices, serviceId]);
      }
    }
  };

  // Handle duration selection
  const handleDurationSelect = (duration) => {
    setSelectedDuration(duration);
    setShowDurationDropdown(false);
  };

  // Get days in month
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month
  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  // Format date
  const formatDate = (date) => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${days[date.getDay()]}, ${date.getDate()} ${
      months[date.getMonth()]
    }`;
  };

  // Helper to show custom alert
  const showCustomAlert = (type, title, message) => {
    setAlertConfig({ type, title, message });
    setShowAlert(true);
  };

  // Check if date is in past
  const isDateInPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  // Handle date selection
  const handleStartDateSelect = (day) => {
    const selected = new Date(selectedYear, selectedMonth, day);

    // Don't allow past dates for start date
    if (isDateInPast(selected)) {
      showCustomAlert('error', 'Invalid Date', 'Please select a future date');
      return;
    }

    setTempStartDate(selected);
    setStartDate(formatDate(selected));
    setShowStartDatePicker(false);
  };

  const handleEndDateSelect = (day) => {
    const selected = new Date(selectedYear, selectedMonth, day);

    // Don't allow dates before start date
    if (tempStartDate && selected < tempStartDate) {
      showCustomAlert('error', 'Invalid Date', 'End date must be after start date');
      return;
    }

    setTempEndDate(selected);
    setEndDate(formatDate(selected));
    setShowEndDatePicker(false);
  };

  // Handle time confirmation
  const handleStartTimeConfirm = () => {
    setStartTime(`${startHour}:${startMinute} ${startPeriod}`);
    setShowStartTimePicker(false);
  };

  const handleEndTimeConfirm = () => {
    setEndTime(`${endHour}:${endMinute} ${endPeriod}`);
    setShowEndTimePicker(false);
  };

  // Navigate months
  const previousMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const nextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  // Generate calendar days
  const generateCalendar = () => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    return days;
  };

  // Convert duration to hours
  const convertDurationToHours = (durationValue) => {
    if (durationValue.endsWith('min')) {
      return parseFloat(durationValue) / 60;
    } else if (durationValue.endsWith('hr')) {
      return parseFloat(durationValue);
    }
    return 0;
  };

  // Convert 12-hour time to 24-hour format
  const convertTo24Hour = (hour, minute, period) => {
    let h = parseInt(hour);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return `${h.toString().padStart(2, '0')}:${minute}`;
  };

  // Convert date to YYYY-MM-DD format
  const convertToISODate = (tempDate) => {
    if (!tempDate) return '';
    const year = tempDate.getFullYear();
    const month = (tempDate.getMonth() + 1).toString().padStart(2, '0');
    const day = tempDate.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Handle submit
  const handleSubmit = async () => {
    try {
      if (pauseType === 'immediate') {
        // Immediate pause
        if (!selectedDuration) {
          showCustomAlert('error', 'Missing Information', 'Please select duration');
          return;
        }
        if (selectedServices.length === 0) {
          showCustomAlert('error', 'Missing Information', 'Please select at least one service');
          return;
        }

        const data = {
          PauseType: 'immediate',
          Duration: convertDurationToHours(selectedDuration),
          ServiceIds: selectedServices,
          Reason: 'Service paused by provider'
        };

        await dispatch(createPause(data)).unwrap();

        // Reset form
        setSelectedDuration('');
        setSelectedServices([]);

        // Refresh data
        dispatch(getPausedServices());
        showCustomAlert('success', 'Success!', 'Pause created successfully!');
      } else {
        // Scheduled pause
        if (!startDate || !startTime || !endDate || !endTime) {
          showCustomAlert('error', 'Missing Information', 'Please fill all date and time fields');
          return;
        }
        if (futureServices.length === 0) {
          showCustomAlert('error', 'Missing Information', 'Please select at least one service');
          return;
        }

        // Validate start date/time is in future
        const startDateTime = new Date(tempStartDate);
        const [startH, startM] = convertTo24Hour(startHour, startMinute, startPeriod).split(':');
        startDateTime.setHours(parseInt(startH), parseInt(startM), 0, 0);

        const now = new Date();
        if (startDateTime <= now) {
          showCustomAlert('error', 'Invalid Time', 'Start date and time must be in the future');
          return;
        }

        // Validate end date/time is after start date/time
        const endDateTime = new Date(tempEndDate);
        const [endH, endM] = convertTo24Hour(endHour, endMinute, endPeriod).split(':');
        endDateTime.setHours(parseInt(endH), parseInt(endM), 0, 0);

        if (endDateTime <= startDateTime) {
          showCustomAlert('error', 'Invalid Time', 'End date and time must be after start date and time');
          return;
        }

        const data = {
          PauseType: 'scheduled',
          StartDate: convertToISODate(tempStartDate),
          StartTime: convertTo24Hour(startHour, startMinute, startPeriod),
          EndDate: convertToISODate(tempEndDate),
          EndTime: convertTo24Hour(endHour, endMinute, endPeriod),
          ServiceIds: futureServices,
          Reason: 'Service scheduled pause'
        };

        await dispatch(createPause(data)).unwrap();

        // Reset form
        setStartDate('');
        setStartTime('');
        setEndDate('');
        setEndTime('');
        setFutureServices([]);
        setTempStartDate(null);
        setTempEndDate(null);

        // Refresh data
        dispatch(getPausedServices());
        showCustomAlert('success', 'Success!', 'Scheduled pause created successfully!');
      }
    } catch (error) {
      showCustomAlert('error', 'Error', 'Failed to create pause. Please try again.');
    }
  };

  // Handle resume - Optimistic Update
  const handleResume = async (pauseId) => {
    // Find the pause data before removing (for potential revert)
    const pauseData = pausedServices.find(p => p.PauseId === pauseId);

    // Step 1: Optimistic update - remove from list instantly
    dispatch(removePausedServiceOptimistic(pauseId));

    try {
      // Step 2: API call in background
      await dispatch(resumeService(pauseId)).unwrap();
      showCustomAlert('success', 'Success!', 'Service resumed successfully!');
    } catch (error) {
      // Step 3: Revert on error - add back to list
      if (pauseData) {
        dispatch(addPausedServiceOptimistic(pauseData));
      }
      showCustomAlert('error', 'Error', 'Failed to resume service. Please try again.');
    }
  };

  // Handle cancel - Optimistic Update
  const handleCancel = async (pauseId) => {
    // Find the pause data before removing (for potential revert)
    const pauseData = scheduledPauses.find(p => p.PauseId === pauseId);

    // Step 1: Optimistic update - remove from list instantly
    dispatch(removeScheduledPauseOptimistic(pauseId));

    try {
      // Step 2: API call in background
      await dispatch(cancelScheduledPause(pauseId)).unwrap();
      showCustomAlert('success', 'Success!', 'Scheduled pause cancelled successfully!');
    } catch (error) {
      // Step 3: Revert on error - add back to list
      if (pauseData) {
        dispatch(addScheduledPauseOptimistic(pauseData));
      }
      showCustomAlert('error', 'Error', 'Failed to cancel pause. Please try again.');
    }
  };

  // Get selected service names
  const getSelectedServiceNames = () => {
    const activeServices =
      pauseType === "immediate" ? selectedServices : futureServices;
    if (activeServices.length === 0) return "Fuel Delivery";
    return services
      .filter((s) => activeServices.includes(s.id))
      .map((s) => s.name)
      .join(", ");
  };

  return (
    <div className="availability-page">
      <Container fluid className="px-4 py-4">
        {/* Error Alert */}
        {error && (
          <Alert variant="danger" dismissible onClose={() => dispatch(clearError())}>
            {error}
          </Alert>
        )}

        {/* Header */}
        <div className="availability-header">
          <h1 className="availability-title">Pause Availability</h1>
          <p className="availability-subtitle">
            Set the services to pause if there is any issue, unavailable for
            bookings or any major concerns.
          </p>
        </div>

        {/* Pause Form Card */}
        <div className="pause-form-card">
          {/* Pause Availability Type */}
          <div className="form-section">
            <h3 className="section-title">Pause Availability</h3>
            <div className="radio-group">
              <label
                className={`radio-option ${
                  pauseType === "immediate" ? "selected" : ""
                }`}
              >
                <input
                  type="radio"
                  name="pauseType"
                  value="immediate"
                  checked={pauseType === "immediate"}
                  onChange={() => setPauseType("immediate")}
                />
                <span className="radio-custom"></span>
                <span className="radio-label">Immediate</span>
              </label>
              <label
                className={`radio-option ${
                  pauseType === "future" ? "selected" : ""
                }`}
              >
                <input
                  type="radio"
                  name="pauseType"
                  value="future"
                  checked={pauseType === "future"}
                  onChange={() => setPauseType("future")}
                />
                <span className="radio-custom"></span>
                <span className="radio-label">Future</span>
              </label>
            </div>
          </div>

          {/* Immediate Pause Fields */}
          {pauseType === "immediate" && (
            <>
              {/* Choose Duration */}
              <div className="form-section dropdown-section" ref={durationRef}>
                <h3 className="section-title">Choose Duration</h3>
                <div
                  className="input-field"
                  onClick={() => setShowDurationDropdown(!showDurationDropdown)}
                >
                  <img
                    src={timeIcon}
                    alt=""
                    className="field-icon"
                    style={{ width: "20px", height: "20px" }}
                  />
                  <span
                    className={
                      selectedDuration
                        ? "field-value-selected"
                        : "field-placeholder"
                    }
                  >
                    {selectedDuration
                      ? durations.find((d) => d.value === selectedDuration)
                          ?.label
                      : "Choose Duration"}
                  </span>
                </div>

                {showDurationDropdown && (
                  <div className="inline-dropdown">
                    {durations.map((duration) => (
                      <label
                        key={duration.id}
                        className={`dropdown-option ${
                          selectedDuration === duration.value ? "selected" : ""
                        }`}
                        onClick={() => handleDurationSelect(duration.value)}
                      >
                        <input
                          type="radio"
                          name="duration"
                          value={duration.value}
                          checked={selectedDuration === duration.value}
                          readOnly
                        />
                        <span className="radio-custom"></span>
                        <span className="option-label">{duration.label}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Choose Service */}
              <div className="form-section dropdown-section" ref={serviceRef}>
                <h3 className="section-title">Choose Service</h3>
                <div
                  className="input-field"
                  onClick={() => setShowServiceDropdown(!showServiceDropdown)}
                >
                  <img
                    src={addressIcon}
                    alt=""
                    className="field-icon"
                    style={{ width: "20px", height: "20px" }}
                  />
                  <span className="field-value-selected">
                    {getSelectedServiceNames()}
                  </span>
                  <svg
                    className="dropdown-arrow"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="#64748B"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>

                {showServiceDropdown && (
                  <div className="inline-dropdown">
                    {services.map((service) => (
                      <label
                        key={service.id}
                        className="dropdown-option checkbox-option custom-checkbox"
                      >
                        <input
                          type="checkbox"
                          checked={selectedServices.includes(service.id)}
                          onChange={() => toggleServiceSelection(service.id)}
                        />
                        <img src={checkedCheckboxIcon} alt="" className="checkbox-icon checked" />
                        <img src={uncheckedCheckboxIcon} alt="" className="checkbox-icon unchecked" />
                        <span className="service-label">{service.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Future Pause Fields */}
          {pauseType === "future" && (
            <>
              {/* Choose Start Date & Time */}
              <Row>
                <Col md={6}>
                  <div
                    className="form-section dropdown-section"
                    ref={startDateRef}
                  >
                    <h3 className="section-title">Choose Start Date</h3>
                    <div
                      className="input-field"
                      onClick={() =>
                        setShowStartDatePicker(!showStartDatePicker)
                      }
                    >
                      <img
                        src={scheduleIcon}
                        alt=""
                        className="field-icon"
                        style={{ width: "20px", height: "20px" }}
                      />
                      <span
                        className={
                          startDate
                            ? "field-value-selected"
                            : "field-placeholder"
                        }
                      >
                        {startDate || "Choose Start Date"}
                      </span>
                    </div>

                    {showStartDatePicker && (
                      <div className="inline-dropdown date-picker-dropdown">
                        <div className="calendar-header">
                          <button className="nav-btn" onClick={previousMonth}>
                            ‹
                          </button>
                          <span className="month-year">
                            {
                              [
                                "Jan",
                                "Feb",
                                "Mar",
                                "Apr",
                                "May",
                                "Jun",
                                "Jul",
                                "Aug",
                                "Sep",
                                "Oct",
                                "Nov",
                                "Dec",
                              ][selectedMonth]
                            }{" "}
                            {selectedYear}
                          </span>
                          <button className="nav-btn" onClick={nextMonth}>
                            ›
                          </button>
                        </div>

                        <div className="calendar-weekdays">
                          {["S", "M", "T", "W", "T", "F", "S"].map(
                            (day, index) => (
                              <div key={index} className="weekday">
                                {day}
                              </div>
                            )
                          )}
                        </div>

                        <div className="calendar-days">
                          {generateCalendar().map((day, index) => {
                            const dayDate = day ? new Date(selectedYear, selectedMonth, day) : null;
                            const isPast = dayDate && isDateInPast(dayDate);

                            return (
                              <div
                                key={index}
                                className={`calendar-day ${
                                  day ? "active" : "empty"
                                } ${
                                  tempStartDate &&
                                  day === tempStartDate.getDate() &&
                                  selectedMonth === tempStartDate.getMonth() &&
                                  selectedYear === tempStartDate.getFullYear()
                                    ? "selected"
                                    : ""
                                } ${isPast ? "disabled" : ""}`}
                                onClick={() => day && !isPast && handleStartDateSelect(day)}
                                style={isPast ? { cursor: 'not-allowed', opacity: 0.4 } : {}}
                              >
                                {day || ""}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <div
                    className="form-section dropdown-section"
                    ref={startTimeRef}
                  >
                    <h3 className="section-title">Choose Start Time</h3>
                    <div
                      className="input-field"
                      onClick={() =>
                        setShowStartTimePicker(!showStartTimePicker)
                      }
                    >
                      <img
                        src={timeIcon}
                        alt=""
                        className="field-icon"
                        style={{ width: "20px", height: "20px" }}
                      />
                      <span
                        className={
                          startTime
                            ? "field-value-selected"
                            : "field-placeholder"
                        }
                      >
                        {startTime || "Choose Start Time"}
                      </span>
                    </div>

                    {showStartTimePicker && (
                      <div className="inline-dropdown time-picker-dropdown">
                        <div className="time-picker-container">
                          <div className="time-input-group">
                            <label className="time-label">hour</label>
                            <input
                              type="number"
                              className="time-input"
                              value={startHour}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (val >= 1 && val <= 12) {
                                  setStartHour(e.target.value.padStart(2, "0"));
                                }
                              }}
                              min="1"
                              max="12"
                            />
                          </div>

                          <span className="time-separator">:</span>

                          <div className="time-input-group">
                            <label className="time-label">minute</label>
                            <input
                              type="number"
                              className="time-input"
                              value={startMinute}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (val >= 0 && val <= 59) {
                                  setStartMinute(
                                    e.target.value.padStart(2, "0")
                                  );
                                }
                              }}
                              min="0"
                              max="59"
                            />
                          </div>

                          <select
                            className="period-select"
                            value={startPeriod}
                            onChange={(e) => setStartPeriod(e.target.value)}
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                        <button
                          className="time-confirm-btn"
                          onClick={handleStartTimeConfirm}
                        >
                          Set Time
                        </button>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Choose End Date & Time */}
              <Row>
                <Col md={6}>
                  <div
                    className="form-section dropdown-section"
                    ref={endDateRef}
                  >
                    <h3 className="section-title">Choose End Date</h3>
                    <div
                      className="input-field"
                      onClick={() => setShowEndDatePicker(!showEndDatePicker)}
                    >
                      <img
                        src={scheduleIcon}
                        alt=""
                        className="field-icon"
                        style={{ width: "20px", height: "20px" }}
                      />
                      <span
                        className={
                          endDate ? "field-value-selected" : "field-placeholder"
                        }
                      >
                        {endDate || "Choose End Date"}
                      </span>
                    </div>

                    {showEndDatePicker && (
                      <div className="inline-dropdown date-picker-dropdown">
                        <div className="calendar-header">
                          <button className="nav-btn" onClick={previousMonth}>
                            ‹
                          </button>
                          <span className="month-year">
                            {
                              [
                                "Jan",
                                "Feb",
                                "Mar",
                                "Apr",
                                "May",
                                "Jun",
                                "Jul",
                                "Aug",
                                "Sep",
                                "Oct",
                                "Nov",
                                "Dec",
                              ][selectedMonth]
                            }{" "}
                            {selectedYear}
                          </span>
                          <button className="nav-btn" onClick={nextMonth}>
                            ›
                          </button>
                        </div>

                        <div className="calendar-weekdays">
                          {["S", "M", "T", "W", "T", "F", "S"].map(
                            (day, index) => (
                              <div key={index} className="weekday">
                                {day}
                              </div>
                            )
                          )}
                        </div>

                        <div className="calendar-days">
                          {generateCalendar().map((day, index) => (
                            <div
                              key={index}
                              className={`calendar-day ${
                                day ? "active" : "empty"
                              } ${
                                tempEndDate &&
                                day === tempEndDate.getDate() &&
                                selectedMonth === tempEndDate.getMonth() &&
                                selectedYear === tempEndDate.getFullYear()
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() => day && handleEndDateSelect(day)}
                            >
                              {day || ""}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Col>
                <Col md={6}>
                  <div
                    className="form-section dropdown-section"
                    ref={endTimeRef}
                  >
                    <h3 className="section-title">Choose Date Time</h3>
                    <div
                      className="input-field"
                      onClick={() => setShowEndTimePicker(!showEndTimePicker)}
                    >
                      <img
                        src={timeIcon}
                        alt=""
                        className="field-icon"
                        style={{ width: "20px", height: "20px" }}
                      />
                      <span
                        className={
                          endTime ? "field-value-selected" : "field-placeholder"
                        }
                      >
                        {endTime || "Choose End Time"}
                      </span>
                    </div>

                    {showEndTimePicker && (
                      <div className="inline-dropdown time-picker-dropdown">
                        <div className="time-picker-container">
                          <div className="time-input-group">
                            <label className="time-label">hour</label>
                            <input
                              type="number"
                              className="time-input"
                              value={endHour}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (val >= 1 && val <= 12) {
                                  setEndHour(e.target.value.padStart(2, "0"));
                                }
                              }}
                              min="1"
                              max="12"
                            />
                          </div>

                          <span className="time-separator">:</span>

                          <div className="time-input-group">
                            <label className="time-label">minute</label>
                            <input
                              type="number"
                              className="time-input"
                              value={endMinute}
                              onChange={(e) => {
                                const val = parseInt(e.target.value);
                                if (val >= 0 && val <= 59) {
                                  setEndMinute(e.target.value.padStart(2, "0"));
                                }
                              }}
                              min="0"
                              max="59"
                            />
                          </div>

                          <select
                            className="period-select"
                            value={endPeriod}
                            onChange={(e) => setEndPeriod(e.target.value)}
                          >
                            <option value="AM">AM</option>
                            <option value="PM">PM</option>
                          </select>
                        </div>
                        <button
                          className="time-confirm-btn"
                          onClick={handleEndTimeConfirm}
                        >
                          Set Time
                        </button>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>

              {/* Choose Service */}
              <div className="form-section dropdown-section" ref={serviceRef}>
                <h3 className="section-title">Choose Service</h3>
                <div
                  className="input-field"
                  onClick={() => setShowServiceDropdown(!showServiceDropdown)}
                >
                  <img
                    src={addressIcon}
                    alt=""
                    className="field-icon"
                    style={{ width: "20px", height: "20px" }}
                  />
                  <span className="field-value-selected">
                    {getSelectedServiceNames()}
                  </span>
                  <svg
                    className="dropdown-arrow"
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M5 7.5L10 12.5L15 7.5"
                      stroke="#64748B"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>

                {showServiceDropdown && (
                  <div className="inline-dropdown">
                    {services.map((service) => (
                      <label
                        key={service.id}
                        className="dropdown-option checkbox-option custom-checkbox"
                      >
                        <input
                          type="checkbox"
                          checked={futureServices.includes(service.id)}
                          onChange={() => toggleServiceSelection(service.id)}
                        />
                        <img src={checkedCheckboxIcon} alt="" className="checkbox-icon checked" />
                        <img src={uncheckedCheckboxIcon} alt="" className="checkbox-icon unchecked" />
                        <span className="service-label">{service.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Submit Button */}
          <button className="submit-btn" onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? 'SUBMITTING...' : 'SUBMIT'}
          </button>
        </div>

        {/* Paused Services & Scheduled Pauses */}
        <Row className="mt-4">
          <Col lg={6}>
            <div className="services-table-card">
              <div className="table-header shedule_pause">
                <h3 className="table-title">Paused Services</h3>
              </div>
              <div className="table-header-row service_pause">
                <div className="header-cell">Services</div>
                <div className="header-cell">Paused From-To</div>
                <div className="header-cell text-right">
                  {pausedServices.length} Active{pausedServices.length !== 1 ? 's' : ''}
                </div>
              </div>
              <div className="table-body pause_body">
                {isLoading ? (
                  <div className="shimmer-pause-loading">
                    {[1, 2, 3].map((item) => (
                      <div key={item} className="table-row service_row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}>
                        <ShimmerBox width="120px" height="14px" />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <ShimmerBox width="140px" height="12px" />
                          <ShimmerBox width="140px" height="12px" />
                        </div>
                        <ShimmerBox width="70px" height="28px" borderRadius="4px" />
                      </div>
                    ))}
                  </div>
                ) : pausedServices.length > 0 ? (
                  pausedServices.map((pause) => (
                    <div key={pause.PauseId} className="table-row service_row">
                      <div className="table-cell service-name">
                        {pause.Services}
                      </div>
                      <div className="table-cell pause-time">
                        <div>{pause.PausedFrom}</div>
                        <div>{pause.PausedTo}</div>
                      </div>
                      <div className="table-cell text-right">
                        <button
                          className="resume-btn"
                          onClick={() => handleResume(pause.PauseId)}
                          disabled={!pause.CanResume || isLoading}
                        >
                          Resume
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="availability-empty-state">
                    <img src={servicesEmptyIcon} alt="No Paused Services" className="empty-state-icon" />
                    <h3 className="empty-state-title">No Paused Services</h3>
                    <p className="empty-state-text">
                      You don't have any paused services. Services you pause will appear here.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className="services-table-card">
              <div className="table-header shedule_pause">
                <h3 className="table-title">Scheduled Pauses</h3>
              </div>
              <div className="table-header-row service_pause">
                <div className="header-cell">Services</div>
                <div className="header-cell">Paused From-To</div>
                <div className="header-cell text-right">
                  {scheduledPauses.length} Upcoming
                </div>
              </div>
              <div className="table-body pause_body">
                {isLoading ? (
                  <div className="shimmer-pause-loading">
                    {[1, 2, 3].map((item) => (
                      <div key={item} className="table-row service_row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}>
                        <ShimmerBox width="120px" height="14px" />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <ShimmerBox width="140px" height="12px" />
                          <ShimmerBox width="140px" height="12px" />
                        </div>
                        <ShimmerBox width="70px" height="28px" borderRadius="4px" />
                      </div>
                    ))}
                  </div>
                ) : scheduledPauses.length > 0 ? (
                  scheduledPauses.map((pause) => (
                    <div key={pause.PauseId} className="table-row service_row">
                      <div className="table-cell service-name">
                        {pause.Services}
                      </div>
                      <div className="table-cell pause-time">
                        <div>{pause.PausedFrom}</div>
                        <div>{pause.PausedTo}</div>
                      </div>
                      <div className="table-cell text-right">
                        <button
                          className="cancel-btn"
                          onClick={() => handleCancel(pause.PauseId)}
                          disabled={!pause.CanCancel || isLoading}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="availability-empty-state">
                    <img src={servicesEmptyIcon} alt="No Scheduled Pauses" className="empty-state-icon" />
                    <h3 className="empty-state-title">No Scheduled Pauses</h3>
                    <p className="empty-state-text">
                      You don't have any scheduled pauses. Future pauses will appear here.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Custom Alert */}
      <CustomAlert
        show={showAlert}
        onClose={() => setShowAlert(false)}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
      />
    </div>
  );
};

export default Availability;
